/**
 * JSON Import Utilities for Prompt Builder
 * Maps frame JSON structure to prompt builder state
 */

import {
  QUALITY_PRESETS,
  ASPECT_RATIOS,
  CAMERA_ANGLES,
  FOCAL_LENGTHS,
  DEPTH_OF_FIELD,
  CAMERA_MOVEMENTS,
  DEFAULT_PROMPT_STATE,
} from './promptPresets'

/**
 * Find closest matching preset ID from a string value
 */
function findPresetId(value, presets, defaultId) {
  if (!value) return defaultId
  
  const normalized = value.toLowerCase().trim()
  
  // Exact ID match
  const exactMatch = presets.find(p => p.id === normalized)
  if (exactMatch) return exactMatch.id
  
  // Name match
  const nameMatch = presets.find(p => p.name.toLowerCase() === normalized)
  if (nameMatch) return nameMatch.id
  
  // Partial match in name
  const partialMatch = presets.find(p => 
    normalized.includes(p.name.toLowerCase()) || 
    p.name.toLowerCase().includes(normalized)
  )
  if (partialMatch) return partialMatch.id
  
  return defaultId
}

/**
 * Detect quality preset from style_preset string
 */
function detectQualityPreset(stylePreset) {
  if (!stylePreset) return 'clean'
  
  const lower = stylePreset.toLowerCase()
  
  if (lower.includes('vhs') || lower.includes('camcorder') || lower.includes('found footage')) {
    return 'vhs'
  }
  if (lower.includes('super 8') || lower.includes('8mm')) {
    return 'super8'
  }
  if (lower.includes('35mm') || lower.includes('film grain')) {
    return 'film35'
  }
  if (lower.includes('polaroid') || lower.includes('instant')) {
    return 'polaroid'
  }
  if (lower.includes('8k') || lower.includes('ultra')) {
    return '8k-ultra'
  }
  if (lower.includes('4k') || lower.includes('cinema')) {
    return '4k-cinema'
  }
  if (lower.includes('broadcast') || lower.includes('tv')) {
    return 'broadcast'
  }
  if (lower.includes('clean') || lower.includes('digital')) {
    return 'clean'
  }
  
  return 'clean'
}

/**
 * Map frame JSON to prompt builder state
 * @param {Object} json - The frame JSON object
 * @returns {Object} - Prompt builder state object
 */
export function mapJsonToPromptState(json) {
  const state = JSON.parse(JSON.stringify(DEFAULT_PROMPT_STATE))
  
  // Project Meta → Quality section
  if (json.project_meta) {
    const meta = json.project_meta
    
    state.sections.quality.enabled = true
    state.sections.quality.collapsed = false
    
    // Aspect ratio
    if (meta.aspect_ratio) {
      const ratioMatch = ASPECT_RATIOS.find(r => r.id === meta.aspect_ratio)
      state.values.quality.aspectRatio = ratioMatch ? ratioMatch.id : '4:3'
    }
    
    // Style preset → detect quality preset + add as style note
    if (meta.style_preset) {
      state.values.quality.preset = detectQualityPreset(meta.style_preset)
      state.values.quality.styleNote = meta.style_preset
    }
    
    // Negative prompt
    if (meta.negative_prompt) {
      state.values.quality.includeNegative = true
      state.values.quality.negativePrompt = meta.negative_prompt
    }
  }
  
  // Subject section
  if (json.subject) {
    const subject = json.subject
    const hasContent = Object.values(subject).some(v => v && v.trim())
    
    if (hasContent) {
      state.sections.subject.enabled = true
      state.sections.subject.collapsed = false
      
      state.values.subject.description = subject.description || ''
      state.values.subject.attire = subject.attire || ''
      state.values.subject.facialFeatures = subject.facial_features || ''
      state.values.subject.props = subject.props || ''
      state.values.subject.consistencyAnchor = subject.consistency_anchor || ''
    }
  }
  
  // Environment section
  if (json.environment) {
    const env = json.environment
    const hasContent = Object.values(env).some(v => v && v.trim())
    
    if (hasContent) {
      state.sections.environment.enabled = true
      state.sections.environment.collapsed = false
      
      state.values.environment.location = env.location || ''
      state.values.environment.lightingGlobal = env.lighting_global || ''
      state.values.environment.lightingAccent = env.lighting_accent || ''
      state.values.environment.backgroundDetails = env.background_details || ''
      state.values.environment.atmosphere = env.atmosphere || ''
      state.values.environment.consistencyAnchor = env.consistency_anchor || ''
    }
  }
  
  // Cinematography section
  if (json.cinematography) {
    const cine = json.cinematography
    const hasContent = Object.values(cine).some(v => v && v.trim())
    
    if (hasContent) {
      state.sections.cinematography.enabled = true
      state.sections.cinematography.collapsed = false
      
      // Map string values to preset IDs
      state.values.cinematography.cameraAngle = findPresetId(
        cine.camera_angle,
        CAMERA_ANGLES,
        'eye-level'
      )
      
      state.values.cinematography.focalLength = findPresetId(
        cine.focal_length,
        FOCAL_LENGTHS,
        '35mm'
      )
      
      state.values.cinematography.depthOfField = findPresetId(
        cine.depth_of_field,
        DEPTH_OF_FIELD,
        'medium'
      )
      
      state.values.cinematography.movement = findPresetId(
        cine.movement,
        CAMERA_MOVEMENTS,
        'static'
      )
      
      state.values.cinematography.cameraPosition = cine.camera_position || ''
    }
  }
  
  // Action/Moment section
  if (json.action_moment) {
    const action = json.action_moment
    const hasContent = Object.values(action).some(v => v && v.trim())
    
    if (hasContent) {
      state.sections.action.enabled = true
      state.sections.action.collapsed = false
      
      state.values.action.pose = action.pose || ''
      state.values.action.expression = action.expression || ''
      state.values.action.dynamicElements = action.dynamic_elements || ''
      state.values.action.interaction = action.interaction || ''
    }
  }
  
  return state
}

/**
 * Get the JSON template with instructions for copying to clipboard
 */
export function getJsonTemplate() {
  return `INSTRUCTION: Fill out the JSON template below to create a frame prompt.
All fields are optional - only fill in what's relevant to your shot.
Save as a .json file, then import it into the Prompt Builder.

{
  "shot_id": "YOUR_SHOT_ID",
  "frame": "start",
  "duration_seconds": 5,
  "project_meta": {
    "aspect_ratio": "4:3",
    "style_preset": "1994 consumer camcorder, VHS feel, found footage",
    "negative_prompt": "clean digital look, perfect stabilization, modern smartphones, cinematic film lighting"
  },
  "subject": {
    "description": "Main subject description - who/what is in frame",
    "attire": "Clothing and costume details",
    "facial_features": "Expression, distinguishing features",
    "props": "Objects the subject is holding or near",
    "consistency_anchor": "Key details to maintain across multiple frames"
  },
  "environment": {
    "location": "Setting description with time period (e.g., 'Video rental shop interior, 1994')",
    "lighting_global": "Main light sources (e.g., 'warm tungsten + fluorescent mix')",
    "lighting_accent": "Secondary lighting (e.g., 'neon sign glow, CRT screen')",
    "background_details": "What's visible behind subject",
    "atmosphere": "Mood and feeling (e.g., 'nostalgic, slightly eerie')",
    "consistency_anchor": "Fixed background elements to maintain"
  },
  "cinematography": {
    "camera_angle": "Eye level, Low angle, High angle, Dutch, POV, Over the shoulder",
    "focal_length": "14mm, 24mm, 35mm, 50mm, 85mm, 135mm, 200mm",
    "depth_of_field": "Shallow, Medium, Deep",
    "camera_position": "Where camera is physically positioned",
    "movement": "Static, Handheld, Dolly, Tracking, Pan, Tilt, Zoom"
  },
  "action_moment": {
    "pose": "Body position and gesture",
    "expression": "Facial expression",
    "dynamic_elements": "Motion blur, particles, etc.",
    "interaction": "What the subject is actively doing"
  }
}`
}

